import { cn } from '@/utils/cn';
import {
  attachmentFileRefsSignature,
  type AttachmentFileRef,
} from '@/utils/agent-file-url';
import { hasNativeJSBridge } from '@/utils/bridge-runtime';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import 'pannellum';
import 'pannellum/build/pannellum.css';
import { Spinner } from '../loading/spinner';
import { Menu } from '@/utils/webview-context-menu';
import { useT } from '@/utils/i18n';
import { EyeSFSymbolMedium } from '@/assets/sf-symbols/medium/eye';
import { SquareAndArrowDownSFSymbolMedium } from '@/assets/sf-symbols/medium/square.and.arrow.down';
import {
  downloadAttachmentSource,
  preparePreviewAsset,
  previewAttachmentSource,
  previewSourceRectForElement,
} from './file-reference-actions';
import {
  localAttachmentSource,
  resolveAttachmentSource,
  type AttachmentNativeSourceAdapter,
} from './attachment-source-resolver';
import {
  diagnoseAttachmentURLOnce,
  previewFailureCopy,
  sourceResolutionFailureCopy,
  type AttachmentURLDiagnosticPayload,
} from './attachment-preview-diagnostics';
import type { ConversationMessageAttachmentImageSize } from '../../types/conversation-message';

type PreviewButtonTone = 'dark' | 'light';

export type AttachmentImageUnavailable = {
  description?: string;
  diagnostic?: AttachmentURLDiagnosticPayload;
  label: string;
  phase: 'preview' | 'source';
  presentation?: 'error' | 'file';
  source?: string | null;
};

function unknownErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return undefined;
}

function normalizedMimeType(value: string | undefined): string | undefined {
  const normalized = value?.split(';', 1)[0]?.trim().toLowerCase();
  return normalized || undefined;
}

function hasSVGExtension(value: string | undefined): boolean {
  const path = value?.split(/[?#]/, 1)[0]?.trim().toLowerCase();
  return path?.endsWith('.svg') ?? false;
}

function isSVGImageAttachment(params: {
  fileName?: string;
  mimeType?: string;
  source?: string;
  sourcePath?: string;
}): boolean {
  return (
    normalizedMimeType(params.mimeType) === 'image/svg+xml' ||
    hasSVGExtension(params.fileName) ||
    hasSVGExtension(params.sourcePath) ||
    hasSVGExtension(params.source)
  );
}

function isLikelyTwoToOneImage(width: number, height: number): boolean {
  if (width <= 0 || height <= 0) {
    return false;
  }

  return Math.abs(width / height - 2) <= 0.01;
}

function has360Keyword(...values: Array<string | undefined>): boolean {
  return values.some(value =>
    /(?:360|equirectangular|pano|panorama|全景)/i.test(value ?? '')
  );
}

function canShow360Entry(
  width: number,
  height: number,
  ...hints: Array<string | undefined>
): boolean {
  return isLikelyTwoToOneImage(width, height) && has360Keyword(...hints);
}

function detectPreviewButtonTone(image: HTMLImageElement): PreviewButtonTone {
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  if (!naturalWidth || !naturalHeight) {
    return 'dark';
  }

  const sampleWidth = Math.min(
    naturalWidth,
    Math.max(48, Math.round(naturalWidth * 0.24))
  );
  const sampleHeight = Math.min(
    naturalHeight,
    Math.max(48, Math.round(naturalHeight * 0.2))
  );
  const sourceX = Math.max(0, naturalWidth - sampleWidth);
  const sourceY = 0;
  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return 'dark';
  }

  try {
    context.drawImage(
      image,
      sourceX,
      sourceY,
      sampleWidth,
      sampleHeight,
      0,
      0,
      sampleWidth,
      sampleHeight
    );

    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    if (!data.length) {
      return 'dark';
    }

    let totalLuminance = 0;
    let totalWeight = 0;

    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3] / 255;
      if (alpha <= 0) {
        continue;
      }

      const red = data[index];
      const green = data[index + 1];
      const blue = data[index + 2];
      totalLuminance += (0.2126 * red + 0.7152 * green + 0.0722 * blue) * alpha;
      totalWeight += alpha;
    }

    if (totalWeight <= 0) {
      return 'dark';
    }

    const averageLuminance = totalLuminance / totalWeight;
    return averageLuminance >= 160 ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function imageActionButtonClassName(tone: PreviewButtonTone, compact = false) {
  return cn(
    'inline-flex min-h-7 items-center rounded-full',
    'text-[0.6875rem] font-medium leading-none shadow-sm ring-1 transition-all duration-150',
    'backdrop-blur-md disabled:cursor-progress disabled:opacity-70',
    compact ? 'size-7 justify-center p-0' : 'gap-1.5 px-2.5 py-1.5',
    tone === 'light'
      ? 'bg-white/55 text-black ring-black/10 shadow-black/10 hover:bg-white/65'
      : 'bg-black/35 text-white ring-white/15 shadow-black/25 hover:bg-black/45'
  );
}

function objectURLFromDataURL(dataURL: string): string | null {
  const separatorIndex = dataURL.indexOf(',');
  if (separatorIndex < 0) {
    return null;
  }

  const metadata = dataURL.slice(0, separatorIndex);
  const payload = dataURL.slice(separatorIndex + 1);
  const mimeTypeMatch = metadata.match(/^data:([^;]+)/i);
  const mimeType = mimeTypeMatch?.[1] ?? 'application/octet-stream';
  const isBase64 = /;base64/i.test(metadata);

  try {
    const binary = isBase64
      ? window.atob(payload)
      : decodeURIComponent(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return window.URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  } catch {
    return null;
  }
}

function PanoramaLightboxViewer({ src }: { src: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const t = useT();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.pannellum?.viewer) {
      return;
    }

    const objectURL = src.startsWith('data:')
      ? objectURLFromDataURL(src)
      : null;
    const panorama = objectURL ?? src;
    const viewer = window.pannellum.viewer(container, {
      type: 'equirectangular',
      panorama,
      autoLoad: true,
      showControls: true,
      showZoomCtrl: true,
      showFullscreenCtrl: false,
      mouseZoom: true,
      draggable: true,
      hfov: 95,
      minHfov: 45,
      maxHfov: 120,
      haov: 360,
      vaov: 180,
      ignoreGPanoXMP: true,
      strings: {
        loadingLabel: t('Loading 360° preview...'),
        genericWebGLError: t('Unable to load the 360° preview.'),
        textureSizeError: t('This panorama is too large to preview here.'),
      },
    });

    return () => {
      viewer.destroy();
      if (objectURL) {
        window.URL.revokeObjectURL(objectURL);
      }
    };
  }, [src, t]);

  return <div ref={containerRef} className="h-full w-full bg-black" />;
}

function ImagePreviewLightbox({
  src,
  fileName,
  mimeType,
  sizeBytes,
  fallbackPath,
  environmentId,
  previewKind,
  canDownload,
  onClose,
}: {
  src: string;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  fallbackPath?: string;
  environmentId?: string;
  previewKind?: 'panorama';
  canDownload: boolean;
  onClose: () => void;
}) {
  const t = useT();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-100 flex flex-col bg-black/88 text-white backdrop-blur-xl"
      role="dialog"
      aria-modal="true"
      aria-label={t('Image preview')}
      onClick={onClose}
    >
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 px-4">
        <div className="min-w-0 truncate text-sm font-medium text-white/90">
          {fileName ?? t('Image preview')}
          {previewKind === 'panorama' ? (
            <span className="ml-2 text-xs font-normal text-white/58">360°</span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canDownload ? (
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white/12 px-3 text-xs font-medium text-white ring-1 ring-white/15 transition-colors hover:bg-white/18"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                void downloadAttachmentSource(src, {
                  fileName,
                  mimeType,
                  sizeBytes,
                  fallbackPath,
                  environmentId,
                });
              }}
            >
              <SquareAndArrowDownSFSymbolMedium className="text-xs" />
              {t('Download')}
            </button>
          ) : null}
          <button
            type="button"
            className="inline-flex h-8 items-center rounded-full bg-white/12 px-3 text-xs font-medium text-white ring-1 ring-white/15 transition-colors hover:bg-white/18"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
            aria-label={t('Close preview')}
          >
            {t('Close')}
          </button>
        </div>
      </div>
      <div
        className="flex min-h-0 flex-1 items-center justify-center p-4"
        onClick={event => event.stopPropagation()}
      >
        {previewKind === 'panorama' ? (
          <PanoramaLightboxViewer src={src} />
        ) : (
          <img
            src={src}
            alt={fileName ?? t('Image preview')}
            className="max-h-full max-w-full object-contain shadow-2xl"
          />
        )}
      </div>
    </div>,
    document.body
  );
}

export const AttachmentImage = ({
  src,
  fileName,
  mimeType,
  sizeBytes,
  sourcePath,
  environmentId,
  sourceSessionId,
  agentId,
  signature,
  fileRefs,
  prompt,
  compactActions = false,
  allowDownload = true,
  cachedImageSize,
  onImageLayout,
  onUnavailable,
  className,
  style,
  ...props
}: {
  className?: string;
  src?: string | null;
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  sourcePath?: string;
  environmentId?: string;
  sourceSessionId?: string;
  agentId?: string;
  signature?: string;
  fileRefs?: ReadonlyArray<AttachmentFileRef> | null;
  prompt?: string;
  compactActions?: boolean;
  allowDownload?: boolean;
  cachedImageSize?: ConversationMessageAttachmentImageSize;
  onImageLayout?: (imageSize: ConversationMessageAttachmentImageSize) => void;
  onUnavailable?: (failure: AttachmentImageUnavailable) => void;
} & React.HTMLAttributes<HTMLElement>) => {
  const t = useT();
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading'
  );
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [previewButtonTone, setPreviewButtonTone] =
    useState<PreviewButtonTone>('dark');
  const [previewKind, setPreviewKind] = useState<'panorama' | undefined>(
    undefined
  );
  const [failureLabel, setFailureLabel] = useState(t('Image unavailable'));
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [localFallbackAttempted, setLocalFallbackAttempted] = useState(false);
  const figureRef = useRef<HTMLElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const diagnosticSourceRef = useRef<string | null>(null);
  const lastReportedImageSizeRef = useRef<string | null>(null);
  const showDownloadAction = allowDownload && !hasNativeJSBridge();
  const fileRefsKey = attachmentFileRefsSignature(fileRefs);
  const stableFileRefsRef = useRef(fileRefs);
  if (attachmentFileRefsSignature(stableFileRefsRef.current) !== fileRefsKey) {
    stableFileRefsRef.current = fileRefs;
  }
  const latestStatusRef = useRef(status);
  latestStatusRef.current = status;
  const latestResolvedSrcRef = useRef(resolvedSrc);
  latestResolvedSrcRef.current = resolvedSrc;
  const cachedAspectRatio =
    cachedImageSize && cachedImageSize.width > 0 && cachedImageSize.height > 0
      ? `${cachedImageSize.width} / ${cachedImageSize.height}`
      : undefined;

  const nativeAttachmentAdapter = useMemo<AttachmentNativeSourceAdapter>(
    () => ({
      resolveAuthenticatedSource: async (source, options) => {
        const bridge = window.jsb?.MessagesBridge;
        if (options.preferDataURL && bridge?.getAttachmentDataURL) {
          return bridge.getAttachmentDataURL(source);
        }
        return bridge?.getStorageDownloadUrl(source) ?? null;
      },
      resolveLocalFileSource: async path =>
        (await window.jsb?.MessagesBridge?.getLocalImageDataURL(path)) ?? null,
    }),
    []
  );

  const resolveLocalImageDataURL = useCallback(async (): Promise<
    string | null
  > => {
    if (!hasNativeJSBridge()) {
      return null;
    }

    const localSource = localAttachmentSource({
      filePath: sourcePath,
      src,
    });

    if (!localSource) {
      return null;
    }

    try {
      return (
        (await nativeAttachmentAdapter.resolveLocalFileSource?.(localSource)) ??
        null
      );
    } catch (err) {
      console.error(
        '[AttachmentImage] Failed to resolve local image path:',
        err
      );
      return null;
    }
  }, [nativeAttachmentAdapter, sourcePath, src]);

  // Resolve remote URLs or local file refs into a browser-displayable source.
  useEffect(() => {
    let cancelled = false;

    const resolveUrl = async () => {
      const localPath = sourcePath?.trim();
      const stableFileRefs = stableFileRefsRef.current;
      const result = await resolveAttachmentSource(
        {
          agentId,
          environmentId,
          filePath: localPath,
          fileRefs: stableFileRefs,
          preferAuthenticatedDataURL: isSVGImageAttachment({
            fileName,
            mimeType,
            source: src?.trim(),
            sourcePath: localPath,
          }),
          signature,
          src,
        },
        {
          nativeAdapter: nativeAttachmentAdapter,
          nativeBridgeAvailable: hasNativeJSBridge(),
        }
      );

      if (!cancelled) {
        if (result.status === 'resolved') {
          const hasVisibleImage =
            latestStatusRef.current === 'loaded' &&
            Boolean(latestResolvedSrcRef.current);
          setLocalFallbackAttempted(result.value.localFallbackUsed);
          setResolvedSrc(result.value.source);
          if (hasVisibleImage) {
            setStatus('loaded');
          }
        } else {
          if (result.failure.error) {
            console.error(
              '[AttachmentImage] Failed to resolve attachment source:',
              result.failure.error
            );
          }
          onUnavailable?.({
            diagnostic: {
              errorCode: result.failure.reason,
              message: unknownErrorMessage(result.failure.error),
            },
            description: sourceResolutionFailureCopy(
              'image',
              result.failure.reason
            ).description,
            label: sourceResolutionFailureCopy('image', result.failure.reason)
              .label,
            phase: 'source',
            source: result.failure.displaySource,
          });
          setStatus('error');
        }
      }
    };

    const hasVisibleImage =
      latestStatusRef.current === 'loaded' &&
      Boolean(latestResolvedSrcRef.current);
    if (!hasVisibleImage) {
      setStatus('loading');
      setResolvedSrc(null);
      setPreviewButtonTone('dark');
      setPreviewKind(undefined);
    }
    setFailureLabel('Image unavailable');
    diagnosticSourceRef.current = null;
    setIsPreviewOpen(false);
    setLocalFallbackAttempted(false);
    resolveUrl();

    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    environmentId,
    fileRefsKey,
    fileName,
    mimeType,
    nativeAttachmentAdapter,
    onUnavailable,
    signature,
    sourcePath,
    src,
  ]);

  // Data URLs (base64) don't need lazy loading
  const isDataUrl = resolvedSrc?.startsWith('data:') ?? false;

  const handleLoad = useCallback(() => {
    setStatus('loaded');

    const image = imageRef.current;
    if (!image) {
      return;
    }

    setPreviewButtonTone(detectPreviewButtonTone(image));
    setPreviewKind(
      canShow360Entry(
        image.naturalWidth,
        image.naturalHeight,
        prompt,
        fileName,
        sourcePath,
        src ?? undefined,
        resolvedSrc ?? undefined
      )
        ? 'panorama'
        : undefined
    );
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      const imageSize = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
      const imageSizeKey = `${imageSize.width}x${imageSize.height}`;
      if (lastReportedImageSizeRef.current !== imageSizeKey) {
        lastReportedImageSizeRef.current = imageSizeKey;
        onImageLayout?.(imageSize);
      }
    }
    if (resolvedSrc) {
      preparePreviewAsset(resolvedSrc, fileName, mimeType, sizeBytes);
    }
  }, [
    fileName,
    mimeType,
    onImageLayout,
    prompt,
    resolvedSrc,
    sizeBytes,
    sourcePath,
    src,
  ]);

  const handleError = useCallback(() => {
    if (
      hasNativeJSBridge() &&
      !localFallbackAttempted &&
      !resolvedSrc?.startsWith('data:')
    ) {
      setLocalFallbackAttempted(true);
      setStatus('loading');
      void (async () => {
        const localResolved = await resolveLocalImageDataURL();
        if (localResolved) {
          setResolvedSrc(localResolved);
          return;
        }

        setStatus('error');
        if (!resolvedSrc || diagnosticSourceRef.current === resolvedSrc) {
          return;
        }
        diagnosticSourceRef.current = resolvedSrc;
        void diagnoseAttachmentURLOnce(resolvedSrc)
          .then(diagnostic => {
            if (diagnosticSourceRef.current === resolvedSrc) {
              const copy = previewFailureCopy('image', diagnostic, {
                fileName,
                mimeType,
              });
              setFailureLabel(copy.description);
              onUnavailable?.({
                description: copy.description,
                diagnostic,
                label: copy.label,
                phase: 'preview',
                source: resolvedSrc,
              });
            }
          })
          .catch(() => {});
      })();
      return;
    }

    setStatus('error');

    if (
      !resolvedSrc ||
      resolvedSrc.startsWith('data:') ||
      diagnosticSourceRef.current === resolvedSrc
    ) {
      return;
    }

    diagnosticSourceRef.current = resolvedSrc;
    void diagnoseAttachmentURLOnce(resolvedSrc)
      .then(diagnostic => {
        if (diagnosticSourceRef.current === resolvedSrc) {
          const copy = previewFailureCopy('image', diagnostic, {
            fileName,
            mimeType,
          });
          setFailureLabel(copy.description);
          onUnavailable?.({
            description: copy.description,
            diagnostic,
            label: copy.label,
            phase: 'preview',
            source: resolvedSrc,
          });
        }
      })
      .catch(() => {});
  }, [
    fileName,
    localFallbackAttempted,
    mimeType,
    onUnavailable,
    resolveLocalImageDataURL,
    resolvedSrc,
  ]);

  useEffect(() => {
    const image = imageRef.current;
    if (!image || !resolvedSrc || status !== 'loading' || !image.complete) {
      return;
    }
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      handleLoad();
    }
  }, [handleLoad, resolvedSrc, status]);

  const previewSourceRect = useCallback(() => {
    return previewSourceRectForElement(imageRef.current ?? figureRef.current);
  }, []);

  const openPreview = useCallback(() => {
    if (!resolvedSrc && !sourcePath) {
      return;
    }

    if (!hasNativeJSBridge() && resolvedSrc) {
      setIsPreviewOpen(true);
      return;
    }

    void previewAttachmentSource(resolvedSrc, {
      fileName,
      mimeType,
      sizeBytes,
      fallbackPath: sourcePath,
      environmentId,
      sourceSessionId,
      sourceRect: previewSourceRect(),
      preferredKind: previewKind,
    });
  }, [
    environmentId,
    fileName,
    mimeType,
    previewKind,
    previewSourceRect,
    resolvedSrc,
    sizeBytes,
    sourceSessionId,
    sourcePath,
  ]);

  const handlePreviewClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openPreview();
    },
    [openPreview]
  );

  const downloadImage = useCallback(async () => {
    if (!resolvedSrc && !sourcePath) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadAttachmentSource(resolvedSrc, {
        fileName,
        mimeType,
        sizeBytes,
        fallbackPath: sourcePath,
        environmentId,
        sourceSessionId,
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    environmentId,
    fileName,
    mimeType,
    resolvedSrc,
    sizeBytes,
    sourcePath,
    sourceSessionId,
  ]);

  const handleDownloadClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void downloadImage();
    },
    [downloadImage]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const menu = Menu.create();
      if (resolvedSrc || sourcePath) {
        menu.pushItem({
          title: t('Preview'),
          icon: Menu.icon.symbol('document.viewfinder'),
          onClick: openPreview,
        });
        if (showDownloadAction) {
          menu.pushSeparator();
          menu.pushItem({
            title: t('Download Image'),
            icon: Menu.icon.symbol('square.and.arrow.down'),
            onClick: () => {
              void downloadImage();
            },
          });
        }
      }

      menu.popup(event);
    },
    [downloadImage, openPreview, resolvedSrc, showDownloadAction, sourcePath, t]
  );

  return (
    <figure
      ref={node => {
        figureRef.current = node;
      }}
      onContextMenu={handleContextMenu}
      className={cn(
        'group/image overflow-hidden rounded-lg border border-black/10 dark:border-white/20 relative',
        'min-h-24 w-fit',
        status !== 'loaded' && !cachedAspectRatio && 'min-w-44 max-w-full',
        className
      )}
      style={{
        ...style,
        ...(cachedAspectRatio ? { aspectRatio: cachedAspectRatio } : {}),
      }}
      data-source-path={sourcePath}
      {...props}
    >
      {(resolvedSrc || sourcePath) && status === 'loaded' && (
        <div
          className={cn(
            'absolute right-3 top-3 z-20 flex max-w-[calc(100%-1.5rem)] items-center gap-1.5',
            'opacity-0 pointer-events-none group-hover/image:opacity-100 group-hover/image:pointer-events-auto',
            'group-focus-within/image:opacity-100 group-focus-within/image:pointer-events-auto',
            'transition-opacity duration-150'
          )}
        >
          <button
            type="button"
            onClick={handlePreviewClick}
            className={imageActionButtonClassName(
              previewButtonTone,
              compactActions
            )}
            aria-label={
              previewKind === 'panorama'
                ? t('Preview 360° image')
                : t('Preview image')
            }
          >
            <EyeSFSymbolMedium className="shrink-0 text-[0.625rem]" />
            {compactActions ? null : (
              <span className="min-w-0 truncate">
                {previewKind === 'panorama' ? '360°' : t('Preview')}
              </span>
            )}
          </button>
          {showDownloadAction ? (
            <button
              type="button"
              onClick={handleDownloadClick}
              disabled={isDownloading}
              className={imageActionButtonClassName(
                previewButtonTone,
                compactActions
              )}
              aria-label={t('Download image')}
            >
              <SquareAndArrowDownSFSymbolMedium className="shrink-0 text-[0.625rem]" />
              {compactActions ? null : (
                <span className="min-w-0 truncate">{t('Download')}</span>
              )}
            </button>
          ) : null}
        </div>
      )}

      {/* Loading placeholder */}
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/5 dark:bg-black/40">
          <Spinner />
        </div>
      )}

      {/* Error state */}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-400 dark:text-gray-500 text-xs p-2">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="max-w-full text-center leading-snug">
            {failureLabel}
          </span>
        </div>
      )}

      {/* Actual image - only render when URL is resolved */}
      {resolvedSrc && (
        <img
          ref={node => {
            imageRef.current = node;
          }}
          src={resolvedSrc}
          alt={fileName ?? 'Uploaded attachment'}
          className={cn(
            'attachment-image',
            'block h-full max-h-[inherit] w-full max-w-full object-contain transition-opacity duration-200',
            status !== 'loaded' && 'opacity-0'
          )}
          loading={isDataUrl ? undefined : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      {isPreviewOpen && resolvedSrc ? (
        <ImagePreviewLightbox
          src={resolvedSrc}
          fileName={fileName}
          mimeType={mimeType}
          sizeBytes={sizeBytes}
          fallbackPath={sourcePath}
          environmentId={environmentId}
          previewKind={previewKind}
          canDownload={showDownloadAction}
          onClose={() => setIsPreviewOpen(false)}
        />
      ) : null}
    </figure>
  );
};
